/**
 * This file contains the main logic to format Chart.js data
 */
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import type { FilterOption } from "components/shared/FiltersAutocomplete";
import { getCountryCode, getCountryName } from "helpers/utils";
import { t } from "i18next";
import { useMemo } from "react";
import type { AnalyticsChartType, TitleSales } from "store/analyticsStore";
import type { IDatesRange } from "types";

import type { AnalyticsOptions } from "../utils";
import { ViewOption } from ".";
import type { BarChartData, DataPoint, DataSet, LineChartData, PieChartData } from "./Chart/utils";

export type TDataSet = {
    label: string;
    data: number[];
    backgroundColor?: string;
    borderColor?: string;
    borderRadius?: number;
    barPercentage?: number;
};

export type ChosenOptions = {
    chosenTitles: FilterOption[];
    chosenChannels: FilterOption[];
    chosenCountryMarkets: FilterOption[];
    chosenDates: IDatesRange;
    dataType: DataType;
    viewOption: FilterOption;
};

export type DataType = "turnover" | "quantities" | "turnover_1" | "turnover_2";

export type AnalyticsMetricItem = {
    turnover?: number | null;
    quantities?: number | null;
    turnover_1?: number | null;
    turnover_2?: number | null;
};

/** Monetary metrics (everything except copies). */
export const isCurrencyDataType = (dataType: DataType): boolean => dataType !== "quantities";

export const getMetricValue = (item: AnalyticsMetricItem, dataType: DataType): number => Number(item[dataType] ?? 0);

export const sortColumnForDataType = (dataType: DataType): string => {
    switch (dataType) {
        case "quantities":
            return "cumulatedQty";
        case "turnover_1":
            return "cumulatedTurnover1";
        case "turnover_2":
            return "cumulatedTurnover2";
        default:
            return "cumulatedRevenue";
    }
};

/**
 * Format Graph data for a type of graph
 * @param Type Graph type
 * @param titleSales Title sales data
 * @param chosenOptions Chosen options (filters)
 * @param filterOptions for filters : titles, channels, country market
 */
export const useChartData = (
    type: AnalyticsChartType,
    titleSales: TitleSales[],
    chosenOptions: ChosenOptions,
    filterOptions: AnalyticsOptions,
) => {
    const { chosenChannels, chosenCountryMarkets, dataType } = chosenOptions;

    return useMemo(() => {
        switch (type) {
            case "line-chart":
                return dataLineTransform(titleSales, chosenOptions, filterOptions);
            case "pie-chart-per-channel":
                return dataPieTransform(titleSales, dataType, chosenChannels, "channelName");
            case "pie-chart-per-country":
                return dataPieTransform(titleSales, dataType, chosenCountryMarkets, "countryMarket");
            case "bar-chart":
                return dataBarTransform(titleSales, chosenOptions, filterOptions);
            default:
                return dataLineTransform(titleSales, chosenOptions, filterOptions);
        }
    }, [type, titleSales, chosenOptions, filterOptions]);
};

/**
 * Transform sale data array into an object with key value equals date label for graph
 * example input : [ sale, sale, sale ]
 * example output : { 2020-12: sale, 2024-10: sale }
 */
const perDateSalesTransform = (titleSales: TitleSales[]) => {
    return titleSales.reduce(
        (acc, sale) => {
            const saleDate = sale.date;
            if (!(saleDate in acc)) {
                acc[saleDate] = sale;
            } else {
                acc[saleDate] = {
                    ...sale,
                    quantities: acc[saleDate].quantities + sale.quantities,
                    turnover: acc[saleDate].turnover + sale.turnover,
                };
            }
            return acc;
        },
        {} as Record<string, (typeof titleSales)[number]>,
    );
};

/**
 * Format data for chart-js Line
 * @param titleSales Title sales data
 * @param chosenOptions Chosen options (filters)
 * @param filterOptions for filters : titles, channels, country market
 */
export const dataLineTransform = (
    titleSales: TitleSales[],
    chosenOptions: ChosenOptions,
    filterOptions: AnalyticsOptions,
) => {
    const chronologicalOrderedSales = chronologicalOrder([...titleSales]);

    const { viewOption, dataType } = chosenOptions;
    const allOptions = {
        chosenTitles: filterOptions.titles,
        chosenChannels: filterOptions.channels,
        chosenCountryMarkets: filterOptions.countryMarkets,
    };
    const { relatedOptionKey, relatedOptionKeyId } = viewOption;

    // Transform data to show only one line for total data
    const singleLineDataTransform = (label: string) => {
        const reducedData = perDateSalesTransform(chronologicalOrderedSales);
        const labels = Object.keys(reducedData);
        const data = Object.values(reducedData).map((sale) => getMetricValue(sale, dataType));
        const datasets = [{ label, backgroundColor: colorSet[0], borderColor: colorSet[0], data }];

        return { labels, datasets } as LineChartData;
    };

    /**
     * Transform data to show multiple line of data on graph. Filtered on viewOption value
     * @param optionKey Chosen data key to display per lines
     * @param optionKeyId Id of option in sales data, example optionKey:
     * "chosenTitles" has for optionKeyId: "titleId"
     * "chosenChannels" has for optionKeyId: "channelId" etc...
     * */
    const multiLineDataTransform = (
        optionKey: "chosenTitles" | "chosenChannels" | "chosenCountryMarkets",
        optionKeyId: "titleId" | "channelId" | "countryMarket",
    ) => {
        let chosenOption = chosenOptions[optionKey];
        // If there is no chosen options (all selected), select all options
        if (!chosenOption.length) chosenOption = allOptions[optionKey];

        const chosenOptionsIds = new Set(chosenOption.map((opt) => opt.id));

        // Get labels for graph first (dates in chronological order)
        const optionSales = chronologicalOrderedSales.filter((sale) => chosenOptionsIds.has(sale[optionKeyId]));
        const salesDatesSet = [...new Set(optionSales.map((sale) => sale.date))];
        const labels = salesDatesSet;

        // Return data by chosen option
        const data = chosenOption.reduce(
            (dataChart, option, i) => {
                const optionSalesData = chronologicalOrderedSales.filter((sale) => sale[optionKeyId] === option.id);

                // only push set if there is sales data
                if (optionSalesData.length > 0) {
                    const salesByDate = perDateSalesTransform(optionSalesData);
                    // Format data by type of sale
                    const data = dataChart.labels.map((date) =>
                        salesByDate?.[date] ? getMetricValue(salesByDate[date], dataType) : 0,
                    );
                    const newDataSets: TDataSet = {
                        label: option.name, // this is legend label
                        data,
                        backgroundColor: colorSet[i],
                        borderColor: colorSet[i],
                    };
                    dataChart.datasets.push(newDataSets);
                }
                return dataChart;
            },
            { labels, datasets: [] as TDataSet[] },
        );
        return data as LineChartData;
    };

    switch (viewOption.id) {
        /**
         * View total sum of selected options
         */
        case ViewOption.TOTAL:
            return singleLineDataTransform(t("Analytics.SumOfMenus"));
        /**
         * Other cases, view by Title / Channel / Country
         */
        default:
            return multiLineDataTransform(relatedOptionKey!, relatedOptionKeyId!);
    }
};

/**
 * Format data for chart-js Pie
 * @param titleSales Title sales data
 * @param dataType Type for data (turnover or quantities)
 * @param chosenOptions Selected options, Channels or Countries
 * @param key data Key used for labels
 */
export const dataPieTransform = (
    titleSales: TitleSales[],
    dataType: DataType,
    chosenOptions: FilterOption[],
    key: "channelName" | "countryMarket",
) => {
    /** Format titleSales data
     * Exemple output for countryMarket key -> { "FR": 14000 }
     * Exemple output for channelName key -> { "Ventes numériques": 20000 }
     * key value can be turnover or quantity
     */
    const saleAmountByLabel = titleSales.reduce(
        (acc, sale) => {
            const metricValue = getMetricValue(sale, dataType);
            if (!metricValue) return acc; // don't count 0 quantities/turnover avoid case { "FR": 0 } that shows label without data
            if (!acc.data[sale[key]]) {
                acc.data[sale[key]] = 0;
            }
            acc.data[sale[key]] += metricValue;
            acc.totalValues += metricValue; // get the total amount of values
            return acc;
        },
        { data: {}, totalValues: 0 },
    );

    const { data: labelsValues, totalValues } = saleAmountByLabel;
    let labels: string[] = [];

    const getLabelsFromOptions = (options) => options.map((opt) => opt.name);
    const getCountryLabelOrDefault = (labels) => labels.map((label) => getCountryName(label)); // it return default labels if it's not a country code

    // Sort labels in descending order by value
    const sortedAmountData = Object.keys(labelsValues).sort((a, b) => labelsValues[b] - labelsValues[a]);
    const sortedOptions = chosenOptions.sort((a, b) => labelsValues[b.id] - labelsValues[a.id]);

    if (!chosenOptions.length) labels = getCountryLabelOrDefault(sortedAmountData);
    else labels = getLabelsFromOptions(sortedOptions);

    const thresholdPercentage = 0.5; // Minimum threshold percentage for a slice to be visible
    let totalOtherValues = 0; // Total of non-visible values for "Other" label

    const data = labels.reduce(
        (dataPie: any, label, index) => {
            let dataKey = label;
            if (key === "countryMarket") {
                dataKey = getCountryCode(label);
            }
            const value = labelsValues[dataKey];
            const valuePercentage = (value / totalValues) * 100;
            const isVisibleValue = valuePercentage > thresholdPercentage;

            // Show visible values
            if (isVisibleValue) {
                dataPie.datasets[0].data.push(labelsValues[dataKey] ?? 0);
                dataPie.datasets[0].backgroundColor.push(colorSet[index] ?? defaultGreyRgba);
            } else {
                // Remove non-visible label
                dataPie.labels = dataPie.labels.filter((item) => item !== label);
                totalOtherValues += value;
                dataPie.dataOthers.push({ label, value });
            }
            return dataPie;
        },
        {
            labels,
            datasets: [{ label: "", data: [], backgroundColor: [] }],
            /**
             * Detailled data for other non-visible values.
             * Retrieve this data in generateLabels() from PieChart.tsx
             */
            dataOthers: [] as DataPoint[],
        },
    );
    if (!chosenOptions.length) {
        if (data.dataOthers.length > 0 && totalOtherValues > 0) {
            data.labels.push(t("Analytics.Others"));
            data.datasets[0].data.push(totalOtherValues);
            data.datasets[0].backgroundColor.push(defaultGreyRgba);
        }
    }
    return data as PieChartData;
};

/**
 * Format data for chart-js Bar
 * @param titleSales Title sales data
 * @param chosenOptions Chosen options (filters)
 * @param filterOptions for filters : titles, channels, country market
 */
export const dataBarTransform = (
    titleSales: TitleSales[],
    chosenOptions: ChosenOptions,
    filterOptions: AnalyticsOptions,
) => {
    const { dataType, viewOption } = chosenOptions;
    const { relatedOptionKey, relatedOptionKeyId } = viewOption;
    const allOptions = {
        chosenTitles: filterOptions.titles,
        chosenChannels: filterOptions.channels,
        chosenCountryMarkets: filterOptions.countryMarkets,
    };

    // Transform data to show only one bar for total data
    const singleBarDataTransform = (label: string) => {
        const totalValues = titleSales.reduce((total, sale) => total + getMetricValue(sale, dataType), 0);
        const data = [totalValues];
        const datasets: TDataSet[] = [
            {
                label,
                backgroundColor: colorSet[0],
                borderColor: colorSet[0],
                borderRadius: 3,
                barPercentage: 0.07,
                data,
            },
        ];
        return { labels: [""], datasets } as BarChartData;
    };

    /**
     * Transform data to show multiple bar of data on graph. Filtered on viewOption value
     * @param optionKey Chosen data key to display per lines
     * @param optionKeyId Id of option in sales data, example optionKey:
     * "chosenTitles" has for optionKeyId: "titleId"
     * "chosenChannels" has for optionKeyId: "channelId" etc...
     * */
    const multiBarDataTransform = (
        optionKey: "chosenTitles" | "chosenChannels" | "chosenCountryMarkets",
        optionKeyId: "titleId" | "channelId" | "countryMarket",
    ) => {
        let chosenOption = chosenOptions[optionKey];
        // If there is no chosen options (all selected), select all options
        if (!chosenOption.length) chosenOption = allOptions[optionKey];

        let dataIndex = 0;
        const data = chosenOption.reduce(
            (dataBar: any, option, index) => {
                // voluntary don't use this index
                const optionSalesData = titleSales.filter((t) => t[optionKeyId] === option.id);
                const newDataSets: TDataSet = {
                    label: option.name,
                    data: [],
                    backgroundColor: colorSet[dataIndex],
                    borderColor: colorSet[dataIndex],
                    borderRadius: 3,
                    barPercentage: 0.07,
                };

                // only push set if there is sales data
                if (!optionSalesData.length) return dataBar;

                let value = 0;
                optionSalesData.forEach((t) => (value += getMetricValue(t, dataType)));
                if (value) {
                    newDataSets.data[dataIndex] = value;
                    dataIndex += 1; // Increment index only if there is sales data
                    dataBar.labels.push(""); // hide X-Axis legend for this graph
                    dataBar.datasets.push(newDataSets);
                }
                return dataBar;
            },
            { labels: [], datasets: [] },
        );
        return data as BarChartData;
    };

    switch (viewOption.id) {
        /**
         * View total sum of selected options
         */
        case ViewOption.TOTAL:
            return singleBarDataTransform(t("Titles.AllTitles"));
        /**
         * Other cases, view by Title / Channel / Country
         */
        default:
            return multiBarDataTransform(relatedOptionKey!, relatedOptionKeyId!);
    }
};

/**
 * Get param Ids for chosen titles, channels, country markets
 */
export const getIds = (
    chosenTitles: FilterOption[],
    chosenChannels?: FilterOption[],
    chosenCountryMarkets?: FilterOption[],
) => {
    // Because titles options are paginated. If all selected, send [] empty param for titleIds .. etc
    let titleIds = chosenTitles.map((t) => +t.id);
    const hasSeletectedAllTitles = chosenTitles.some((t) => t?.id === "all");
    if (hasSeletectedAllTitles) titleIds = [];

    let channelIds = chosenChannels?.map((c) => +c?.id);
    const hasSelectedAllChannels = chosenChannels?.some((c) => c?.id === "all");
    if (hasSelectedAllChannels) channelIds = [];

    let countryCodes = chosenCountryMarkets?.map((cm) => cm.id);
    const hasSelectedAllCountryCodes = chosenCountryMarkets?.some((cm) => cm.id === "all");
    if (hasSelectedAllCountryCodes) countryCodes = [];

    return {
        titleIds,
        channelIds,
        countryCodes,
    };
};

const isValidDate = (date: string): boolean => !isNaN(new Date(date).valueOf());
// Convert numerical date or return -Infinity if i's invalid
const parseDate = (date: string): number => {
    const timestamp = new Date(date).valueOf();
    return isNaN(timestamp) ? -Infinity : timestamp;
};
/**
 * Sort sales by chronological order
 * If the value isn't a valid date it returns "N/A" at the start of array
 **/
const chronologicalOrder = (titleSales: TitleSales[]) => {
    return titleSales
        .sort((a, b) => {
            const dateA = parseDate(a.date);
            const dateB = parseDate(b.date);
            return dateA - dateB;
        })
        .map((sale) => ({
            ...sale,
            date: isValidDate(sale.date) ? sale.date : "N/C",
        }));
};

/**
 * This is a pastel color set
 */
export const colorSet = [
    "rgba(230, 230, 250, 0.8)", // Lavender
    "rgba(135, 206, 250, 0.8)", // Light Sky Blue
    "rgba(221, 160, 221, 0.8)", // Plum
    "rgba(169, 169, 169, 0.8)", // Dark Gray
    "rgba(219, 112, 147, 0.8)", // Pale Violet Red
    "rgba(211, 211, 211, 0.8)", // Light Gray
    "rgba(123, 104, 238, 0.8)", // Medium Slate Blue
    "rgba(105, 105, 105, 0.8)", // Dim Gray
    "rgba(192, 192, 192, 0.8)", // Silver
    "rgba(144, 238, 144, 0.8)", // Light Green
    "rgba(255, 182, 193, 0.8)", // Light Pink
    "rgba(250, 128, 114, 0.8)", // Salmon
    "rgba(255, 222, 173, 0.8)", // Navajo White
    "rgba(240, 230, 140, 0.8)", // Khaki
    "rgba(175, 238, 238, 0.8)", // Pale Turquoise
    "rgba(255, 239, 213, 0.8)", // Papaya Whip
    "rgba(221, 245, 221, 0.8)", // Mint Cream
];

export const defaultGreyRgba = "rgba(169, 169, 169, 0.2)";

/**
 * View Options List, used for line-chart
 */
export const viewOptions = (chartType: Extract<AnalyticsChartType, "line-chart" | "bar-chart">) => {
    const options: Record<string, FilterOption[]> = {
        "line-chart": [
            {
                id: 1,
                name: "Total",
                icon: TimelineOutlinedIcon,
                description: t("Analytics.SingleLineDescription"),
                value: 1,
                label: "Total",
            },
            {
                id: 2,
                name: t("Analytics.ViewByTitle"),
                icon: StackedLineChartRoundedIcon,
                description: t("Analytics.ViewByTitleDescription"),
                relatedOptionKey: "chosenTitles",
                relatedOptionKeyId: "titleId",
                value: 2,
                label: t("Analytics.ViewByTitle"),
            },
            {
                id: 3,
                name: t("Analytics.ViewByChannel"),
                icon: StackedLineChartRoundedIcon,
                description: t("Analytics.ViewByChannelDescription"),
                relatedOptionKey: "chosenChannels",
                relatedOptionKeyId: "channelId",
                value: 3,
                label: t("Analytics.ViewByChannel"),
            },
            {
                id: 4,
                name: t("Analytics.ViewByCountry"),
                icon: StackedLineChartRoundedIcon,
                description: t("Analytics.ViewByCountryDescription"),
                relatedOptionKey: "chosenCountryMarkets",
                relatedOptionKeyId: "countryMarket",
                value: 4,
                label: t("Analytics.ViewByCountry"),
            },
        ],
        "bar-chart": [
            {
                id: 1,
                name: "Total",
                icon: LooksOneOutlinedIcon,
                description: t("Analytics.SingleBarDescription"),
                value: 1,
                label: "Total",
            },
            {
                id: 2,
                name: t("Analytics.ViewByTitle"),
                icon: BarChartOutlinedIcon,
                description: t("Analytics.ViewByTitleBarDescription"),
                relatedOptionKey: "chosenTitles",
                relatedOptionKeyId: "titleId",
                value: 2,
                label: t("Analytics.ViewByTitle"),
            },
            {
                id: 3,
                name: t("Analytics.ViewByChannel"),
                icon: BarChartOutlinedIcon,
                description: t("Analytics.ViewByChannelBarDescription"),
                relatedOptionKey: "chosenChannels",
                relatedOptionKeyId: "channelId",
                value: 3,
                label: t("Analytics.ViewByChannel"),
            },
            {
                id: 4,
                name: t("Analytics.ViewByCountry"),
                icon: BarChartOutlinedIcon,
                description: t("Analytics.ViewByCountryBarDescription"),
                relatedOptionKey: "chosenCountryMarkets",
                relatedOptionKeyId: "countryMarket",
                value: 4,
                label: t("Analytics.ViewByCountry"),
            },
        ],
    };

    return options[chartType];
};

type TagsLimit = { titles: number; channels: number; countryMarkets: number };
type LimitByViewOption = Record<ViewOption, TagsLimit>;
/**
 * Get the number limit of tags for autocomplete, based on chart type and chosen view option
 */
export const useTagsLimit = (chartType: AnalyticsChartType, viewOptionId: ViewOption) => {
    const defaultLimits = { titles: 3, channels: 3, countryMarkets: 3 };
    const limitsByViewOption: LimitByViewOption = {
        [ViewOption.TOTAL]: defaultLimits,
        [ViewOption.BY_TITLE]: { ...defaultLimits, titles: 1 },
        [ViewOption.BY_CHANNEL]: { ...defaultLimits, channels: 1 },
        [ViewOption.BY_COUNTRY]: { ...defaultLimits, countryMarkets: 1 },
    };
    const tagsLimits = {
        "line-chart": limitsByViewOption,
        "pie-chart-per-channel": { ...defaultLimits, channels: 1 },
        "pie-chart-per-country": { ...defaultLimits, countryMarkets: 1 },
        "bar-chart": limitsByViewOption,
    } as Record<AnalyticsChartType, LimitByViewOption | TagsLimit>;

    return useMemo(() => {
        if (["line-chart", "bar-chart"].includes(chartType)) return tagsLimits[chartType][viewOptionId] as TagsLimit;
        return tagsLimits[chartType] as TagsLimit;
    }, [viewOptionId]);
};

export const formatDataSet = ({
    data: { label, data, dataOthers },
    dataSetIndex,
    chartType,
}: {
    data: {
        label: string;
        data: Array<{ label: string; value: number }>;
        dataOthers: DataSet["dataOthers"];
    };
    dataSetIndex: number;
    chartType: AnalyticsChartType;
}) => {
    const backgroundColor =
        chartType === "line-chart"
            ? colorSet[dataSetIndex]
            : data
                  .map((point, i) => (point.label === t("Analytics.Others") ? defaultGreyRgba : colorSet[i]))
                  .filter(Boolean);

    return {
        label: label,
        data: data.map((o) => o.value),
        dataOthers: dataOthers,
        fill: false,
        pointHoverBackgroundColor: "#000000",
        borderColor: chartType === "line-chart" ? colorSet[dataSetIndex] : undefined,
        backgroundColor,
        borderRadius: chartType === "bar-chart" ? 3 : undefined,
        barPercentage: chartType === "bar-chart" ? 0.07 : undefined,
        tension: 0.2,
    };
};

export const plugMissingDates = (
    chartPoints: {
        value: number;
        label: string;
    }[],
    periodsDateRange: IDatesRange,
): { value: number; label: string }[] => {
    const { startDate, endDate } = periodsDateRange;
    const start = new Date(startDate);
    const end = new Date(endDate);

    // All dates between start and end as "YYYY-MM"
    const dateLabels: string[] = [];

    for (let d = start; d <= end; d.setMonth(d.getMonth() + 1)) {
        dateLabels.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }

    return dateLabels.map((label) => {
        const point = chartPoints.find((point) => point.label === label);
        return point || { value: 0, label };
    });
};
